import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

import cx from 'classnames';
import { createPortal } from 'react-dom';

import { SearchIcon } from 'assets/icons';
import { translate } from 'utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LookupOption {
  id: string;
  name: string;
  /** Optional account currency when the suggestion is an account lookup */
  currencyIsoCode?: string | null;
}

export interface LookupFieldProps {
  /** Form field label */
  label: string;
  required?: boolean;
  /** Currently stored ID value */
  value: string;
  /**
   * Display name for the currently stored ID.
   * Pass the human-readable name you want shown in the input on initial render
   * (e.g. `lead.leadOwner` for the owner field in edit mode).
   */
  displayName?: string;
  /** Called when the user selects a suggestion — receives id, name, and the full option */
  onChange: (id: string, name: string, option: LookupOption) => void;
  /** Called when the user clears the field */
  onClear: () => void;
  /** Called when the user clicks Search (or presses Enter). Parent should call the API here. */
  fetchSuggestions: (search: string) => void;
  suggestions: LookupOption[];
  loading?: boolean;
  error?: string;
  placeholder?: string;
}

const MENU_MAX_HEIGHT_PX = 192; // max-h-48
const MENU_GAP_PX = 4; // mt-1
/** Extra space required below before opening downward (modal footer, gutter). */
const MENU_FLIP_CLEARANCE_PX = 10;

type MenuCoords = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
};

// ─── Component ────────────────────────────────────────────────────────────────

const LookupField: FC<LookupFieldProps> = ({
  label,
  required,
  value,
  displayName = '',
  onChange,
  onClear,
  fetchSuggestions,
  suggestions,
  loading = false,
  error,
  placeholder
}) => {
  const [inputText, setInputText] = useState(displayName);
  const [open, setOpen] = useState(false);
  // Tracks the display name of the last confirmed selection so we can detect
  // when the user edits the text away from it without picking a new option.
  const [confirmedName, setConfirmedName] = useState(displayName);
  // Tracks which query was last searched vs which query the current suggestions
  // belong to, so stale results from a previous search are never shown.
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  const [resolvedQuery, setResolvedQuery] = useState<string | null>(null);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);

  const fieldRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isResultsStale = searchedQuery !== resolvedQuery;

  const updateMenuPosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP_PX;
    const spaceAbove = rect.top - MENU_GAP_PX;
    const placeAbove =
      spaceBelow < MENU_MAX_HEIGHT_PX + MENU_FLIP_CLEARANCE_PX &&
      spaceAbove > spaceBelow;

    if (placeAbove) {
      setMenuCoords({
        bottom: window.innerHeight - rect.top + MENU_GAP_PX,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(MENU_MAX_HEIGHT_PX, Math.max(0, spaceAbove))
      });
    } else {
      setMenuCoords({
        top: rect.bottom + MENU_GAP_PX,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(MENU_MAX_HEIGHT_PX, Math.max(0, spaceBelow))
      });
    }
  }, []);

  // Keep input in sync when the parent updates the display name (e.g. on reset)
  useEffect(() => {
    setInputText(displayName);
    setConfirmedName(displayName);
    setSearchedQuery(null);
    setResolvedQuery(null);
    setOpen(false);
  }, [displayName]);

  useEffect(() => {
    if (!loading && searchedQuery !== null) {
      setResolvedQuery(searchedQuery);
    }
  }, [loading, searchedQuery]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuCoords(null);
      return;
    }
    updateMenuPosition();
  }, [open, updateMenuPosition, suggestions, loading, isResultsStale]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        fieldRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const closeOnScrollUnlessMenuList = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('[data-lookup-menu-list]')
      ) {
        return;
      }
      setOpen(false);
    };

    const handleResize = () => updateMenuPosition();

    document.addEventListener('scroll', closeOnScrollUnlessMenuList, true);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('scroll', closeOnScrollUnlessMenuList, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open, updateMenuPosition]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setInputText(text);
    setOpen(false);

    if (text.trim()) {
      // If the user has edited away from the confirmed selection, clear the
      // stored ID so a stale ID is never submitted.
      if (value && text !== confirmedName) {
        onClear();
      }
    } else {
      onClear();
    }
  };

  // Revert to the last confirmed name when the user blurs without selecting
  // a new option, so the field is never left empty after an edit.
  const handleBlur = () => {
    if (!value) return;
    setTimeout(() => {
      if (menuRef.current?.contains(document.activeElement)) return;
      if (inputText !== confirmedName) {
        setInputText(confirmedName);
      }
    }, 0);
  };

  const handleSearch = () => {
    const search = inputText.trim();
    if (!search) return;
    setResolvedQuery(null);
    setSearchedQuery(search);
    fetchSuggestions(search);
    setOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelect = (option: LookupOption) => {
    setInputText(option.name);
    setConfirmedName(option.name);
    setOpen(false);
    onChange(option.id, option.name, option);
  };

  const handleClear = () => {
    setInputText('');
    setConfirmedName('');
    setSearchedQuery(null);
    setResolvedQuery(null);
    setOpen(false);
    onClear();
  };

  const menu =
    open && menuCoords
      ? createPortal(
          <div
            ref={menuRef}
            data-lookup-menu-list=''
            className='fixed z-[9999] bg-white border border-LIGHT_GRAY rounded-lg shadow-lg overflow-y-auto'
            style={{
              top: menuCoords.top,
              bottom: menuCoords.bottom,
              left: menuCoords.left,
              width: menuCoords.width,
              maxHeight: menuCoords.maxHeight
            }}
          >
            {loading || isResultsStale ? (
              <div className='px-3 py-2 text-sm text-DISABLED'>
                {translate('patient_list.loading_data')}
              </div>
            ) : suggestions.length === 0 ? (
              <div className='px-3 py-2 text-sm text-DISABLED'>
                {translate('common.no_results')}
              </div>
            ) : (
              suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type='button'
                  className='w-full text-left px-3 py-2 text-sm hover:bg-NEUTRAL_50 text-DEFAULT_TEXT'
                  onClick={() => handleSelect(suggestion)}
                >
                  {suggestion.name}
                </button>
              ))
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div className='flex flex-col'>
      <label className='text-xs text-DISABLED mb-1'>
        {label}
        {required && <span className='text-ERROR ml-0.5'>*</span>}
      </label>

      <div ref={fieldRef} className='flex gap-2'>
        <div ref={anchorRef} className='relative flex-1'>
          <input
            type='text'
            className={cx(
              'w-full border rounded-lg px-3 py-2.5 pr-8 text-sm text-DEFAULT_TEXT focus:outline-none focus:border-PRIMARY',
              error ? 'border-ERROR' : 'border-LIGHT_GRAY'
            )}
            value={inputText}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={placeholder ?? label}
          />

          {(inputText || value) && (
            <button
              type='button'
              onClick={handleClear}
              className='absolute right-2 top-1/2 -translate-y-1/2 text-DISABLED hover:text-DEFAULT_TEXT'
              aria-label={translate('common.clear')}
            >
              <svg
                className='w-4 h-4'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          )}
        </div>

        <button
          type='button'
          onClick={handleSearch}
          disabled={!inputText.trim() || loading || isResultsStale}
          className={cx(
            'shrink-0 flex items-center gap-1.5 border rounded-lg px-3 py-2.5 text-sm',
            'border-LIGHT_GRAY text-DEFAULT_TEXT hover:border-PRIMARY hover:text-PRIMARY',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-LIGHT_GRAY disabled:hover:text-DEFAULT_TEXT'
          )}
          aria-label={translate('search.search')}
        >
          <SearchIcon className='w-4 h-4' />
          <span className='hidden sm:inline'>{translate('search.search')}</span>
        </button>
      </div>

      {menu}

      {error && <span className='text-xs text-ERROR mt-1'>{error}</span>}
    </div>
  );
};

export default LookupField;
